import React from 'react';
import Joi from 'joi';
import JoiPhoneNumber from 'joi-phone-number';

import { connect } from 'react-redux';
import { loadDivisions } from '../../store/divisions';
import { Organization, Role, SelectOption, User } from '../../utils/models';
import { USER_TYPES } from '../../utils/constants';
import { AppDispatch, RootState } from '../../store/configureStore';

import Conditional from '../common/conditional';
import Form from '../common/form';
import Progress from '../themed/progress';

import * as userService from '../../services/userService';
import * as toastService from '../../services/toastService';

import * as data from '../../utils/data';
import * as utils from '../../utils/utilities';

import withRoles from '../hoc/withRoles';
import withOrganizations from '../hoc/withOrganizations';

const countryCode: string = process.env.REACT_APP_COUNTRY_CODE as string;
const customJoiPhone = Joi.extend(JoiPhoneNumber);
interface UserData {
	id: string;
	firstName: string;
	lastName: string;
	phone: string;
	email: string;
	roleId: string;
	division: SelectOption[];
	designation: string;
	userType: string;
	organisationId: string;
	facilityId: string;
}

class UpdateUser extends Form {
	state = {
		data: initialUserData,
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		id: Joi.string().required().label('First Name'),
		firstName: Joi.string().min(3).required().label('First Name'),
		lastName: Joi.string().min(3).required().label('Last Name'),
		email: Joi.string()
			.email({ tlds: false })
			.required()
			.label('Email Address'),
		roleId: Joi.string().required().label('Role'),
		phone: customJoiPhone
			.string()
			.allow('')
			.optional()
			.phoneNumber({ defaultCountry: countryCode, strict: true })
			.label('Contact Phone number'),
		userType: Joi.string().required().label('User Type'),
		division: Joi.any().when('userType', {
			is: USER_TYPES.INTERNAL,
			then: Joi.array()
				.min(0)
				.items(
					Joi.object({
						id: Joi.string().required(),
						name: Joi.string().required(),
					})
				)
				.required()
				.label('Division'),
			otherwise: Joi.optional().label('Division'),
		}),
		designation: Joi.string().min(3).required().label('Designation'),
		facilityId: Joi.any().when('userType', {
			is: USER_TYPES.EXTERNAL,
			then: Joi.string().required().label('Facility ID'),
		}),
		organisationId: Joi.any().when('userType', {
			is: USER_TYPES.EXTERNAL,
			then: Joi.string().required().label('Organization ID'),
		}),
	};

	componentDidMount() {
		this.props.loadDivisions();
		this.resetUser();
	}

	getApiData = () => {
		const apiData: any = { ...this.state.data };
		const isExternalUser = apiData.userType === USER_TYPES.EXTERNAL;

		if (isExternalUser) delete apiData.division;
		else {
			apiData.division = apiData.division.map((d: SelectOption) => d.id);
			delete apiData.facilityId;
		}

		return apiData;
	};

	getUserDivisions = (): SelectOption[] => {
		const user = this.props.user as User;

		const isExternalUser = user.userType === USER_TYPES.EXTERNAL;
		if (isExternalUser) return [];

		return user.division.map((id: string, index: number) => ({
			id,
			name: this.props.user.divisionName[index],
		}));
	};

	getUserFacility = () => {
		const organizations: Organization[] = this.props.organizations;
		if (this.state.data.userType === USER_TYPES.EXTERNAL) {
			const selectedOrganization = organizations.find(
				(o) => o._id === this.state.data.organisationId
			);
			if (selectedOrganization)
				return selectedOrganization.facilities.map(utils.transformFacilityItem);
		}

		return [];
	};

	postData = async (): Promise<void> => {
		const apiData = this.getApiData();

		await userService.updateUser(apiData, this.handleProgressChange);
		window.location.reload();
	};

	resetUser = (): void => {
		if (this.props.user) {
			this.setState({
				data: {
					id: this.props.user.id,
					firstName: this.props.user.firstName,
					lastName: this.props.user.lastName,
					phone: this.props.user.phone,
					email: this.props.user.email,
					roleId: this.props.user.roleId,
					division: this.getUserDivisions(),
					designation: this.props.user.designation,
					userType: this.props.user.userType,
					organisationId: this.props.user.organisationId,
					facilityId: this.props.user.facilityId,
				},
			});
		}
	};

	doSubmit = (): void => {
		toastService.promise(this.postData(), {
			loading: 'Updating User',
			success: 'User Information Updated',
			error: utils.getErrorMessage,
		});
	};

	render() {
		const { userType } = this.state.data;

		return (
			<React.Fragment>
				<Progress progress={this.state.progress} />

				<form autoComplete="off" onSubmit={this.handleSubmit}>
					<h1 className="form-title">Update User Information</h1>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'firstName',
								label: 'First Name',
								placeholder: 'e.g. John',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'lastName',
								label: 'Last Name',
								placeholder: 'e.g. Doe',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'phone',
								label: 'Phone Number',
								placeholder: 'e.g. 08142317489',
							})}
						</div>
					</div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'email',
								name: 'email',
								label: 'Email Address',
								placeholder: 'e.g. jdoe@russelsmithgroup.com',
							})}
						</div>

						<Conditional isVisible={userType === USER_TYPES.INTERNAL}>
							<div className="col">
								{this.renderMultiSelect({
									options: this.props.divisions,
									name: 'division',
									label: 'Division',
									placeholder: 'Select Division',
									showCheckbox: true,
									loading: false,
								})}
							</div>
						</Conditional>
					</div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'designation',
								label: 'Designation',
								placeholder: 'Enter Designation',
							})}
						</div>
						<div className="col">
							{this.renderSelect({
								name: 'roleId',
								label: 'Role',
								placeholder: 'Select Role',
								options: this.props.roles.filter((role: Role) => role.userType === this.state.data.userType),
							})}
						</div>
						<div className="col">
							{this.renderSelect({
								name: 'userType',
								label: 'User Type',
								placeholder: 'Select User Type',
								disabled: true,
								options: data.getUserTypes(),
							})}
						</div>
					</div>

					<Conditional isVisible={userType === USER_TYPES.EXTERNAL}>
						<div className="row">
							<div className="col">
								{this.renderSelect({
									name: 'organisationId',
									label: 'Organization ID',
									placeholder: 'Select Organization',
									disabled: true,
									options: this.props.organizationsForDropdown,
								})}
							</div>

							<div className="col">
								{this.renderSelect({
									name: 'facilityId',
									label: 'Facility ID',
									placeholder: 'Select Facility',
									disabled: true,
									options: this.getUserFacility(),
								})}
							</div>
						</div>
					</Conditional>

					<div className="d-flex justify-content-end">
						{this.renderButton('Update')}
					</div>
				</form>
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: RootState) => {
	return {
		divisions: state.divisions.list.map(utils.transformDivisionItem),
	};
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
	return {
		loadDivisions: () => dispatch(loadDivisions()),
	};
};

const initialUserData: UserData = {
	id: '',
	firstName: '',
	lastName: '',
	phone: '',
	email: '',
	roleId: '',
	division: [],
	designation: '',
	userType: '',
	organisationId: '',
	facilityId: '',
};

const Roles = withRoles(withOrganizations(UpdateUser));
export default connect(mapStateToProps, mapDispatchToProps)(Roles);
